
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { IHandle } from '@jeppesen-foreflight/jmkw-types';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  OnDestroy,
  signal
} from '@angular/core';
import Point from '@arcgis/core/geometry/Point';
import { AbstractButton, ButtonComponent } from '@jeppesen-foreflight/jmkw-toolkit';
import { ButtonParams, JepGraphicsLayer } from '@jeppesen-foreflight/jmkw-types';


@Component({
  selector: 'jmkw-location-button',
  standalone: true,
  imports: [ButtonComponent],
  styleUrls: ['./location.button.scss'],
  templateUrl: './location.button.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationButtonComponent extends AbstractButton implements OnDestroy{
  tooltipText = 'Toggle Location';
  isActive = computed(() => this.locationIconName() === 'icon-location_on');
  locationIconName = signal<'icon-location_on' | 'icon-location_off' | 'icon-not_listed_location'>(
    'icon-location_off'
  );
  movementHandler: IHandle | undefined;



  onClick(event: MouseEvent) {
    navigator.geolocation?.getCurrentPosition(
      (pos) => this._handlePosition(pos.coords, event),
      undefined,
      { enableHighAccuracy: false, timeout: 5000, maximumAge: Infinity }
    );
  }

  private _handlePosition(coords: GeolocationCoordinates, event: MouseEvent) {
    if (this.isActive()) {
      this._deactivate();
    } else {
      this._activate(coords);
    }
    this.toolkitApiService.dispatchEvent({
      sourceId: 'LocationButton',
      source: this,
      event
    });
  }

  private _activate(coords: GeolocationCoordinates) {
    this.locationIconName.set('icon-location_on');
    this._setLayerVisibility(true);
    this._updateIndicator(coords);
    this._moveViews(coords);
    if(this.movementHandler) this.movementHandler.remove();
    this.movementHandler = reactiveUtils.watch(
      () => this.toolkitApiService.getMapView().center,
      () => this._onMovement(coords));
  }

  private _onMovement({latitude, longitude} : GeolocationCoordinates) {
    if(latitude.toPrecision(7) != this.toolkitApiService.getMapView().center.latitude?.toPrecision(7) ||
       longitude.toPrecision(7) != this.toolkitApiService.getMapView().center.longitude?.toPrecision(7)) {
      if (this.movementHandler) this.movementHandler.remove();
      this.locationIconName.set('icon-not_listed_location');
    }
  }

  private _deactivate() {
    if (this.movementHandler) this.movementHandler.remove();
    this.locationIconName.set('icon-location_off');
    this._setLayerVisibility(false);
  }

  private _setLayerVisibility(visible: boolean) {
    const layer =
      this.toolkitApiService.getLayerById<JepGraphicsLayer>('location');
    if (layer) {
      layer.visible = visible;
    }
  }

  private _updateIndicator({ latitude, longitude }: GeolocationCoordinates) {
    const layer =
      this.toolkitApiService.getLayerById<JepGraphicsLayer>('location');
    if (layer) {
      const graphic = layer.graphics.getItemAt(0);
      if (graphic) {
        graphic.geometry = new Point({ latitude, longitude });
      }
    }
  }

  private _moveViews({ latitude, longitude }: GeolocationCoordinates) {
    this.toolkitApiService.moveMapView(latitude, longitude);
    this.toolkitApiService.moveSceneView(latitude, longitude);

  }

  ngOnDestroy(): void {
    this._deactivate();
  }
}

export const LOCATION_BUTTON: ButtonParams = {
  iconName: 'icon-location_off',
  tooltipText: 'Toggle Location',
  buttonComponent: LocationButtonComponent
};
